import { useEffect, useState, type FormEvent } from 'react';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import Modal from '@/components/base/Modal';
import { useBlocker } from '@/hooks/useBlocker';
import { loadDraft, saveDraft, clearDraft } from '@/lib/draftStorage';

interface BranchForm {
  name: string;
  legal_name: string;
  rnc: string;
  email: string;
  website: string;
  description: string;
  address: string;
  city: string;
  province: string;
  country: string;
  phone: string;
  whatsapp: string;
  logo_url: string;
  latitude: string;
  longitude: string;
}

const emptyForm: BranchForm = {
  name: '',
  legal_name: '',
  rnc: '',
  email: '',
  website: '',
  description: '',
  address: '',
  city: '',
  province: '',
  country: 'República Dominicana',
  phone: '',
  whatsapp: '',
  logo_url: '',
  latitude: '',
  longitude: '',
};

export default function BranchDataPage() {
  const { branches, activeBranchId, activeBranch, setActiveBranchId, reload } = useBranch();
  const [form, setForm] = useState<BranchForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loadedBranchId, setLoadedBranchId] = useState<string | null>(null);

  const draftKey = activeBranch ? `branch:data:draft:${activeBranch.id}` : null;

  const blocker = useBlocker(dirty);

  useEffect(() => {
    if (activeBranch) {
      const key = `branch:data:draft:${activeBranch.id}`;
      const draft = loadDraft<BranchForm>(key);
      setForm(
        draft ?? {
          name: activeBranch.name ?? '',
          legal_name: activeBranch.legal_name ?? '',
          rnc: activeBranch.rnc ?? '',
          email: activeBranch.email ?? '',
          website: activeBranch.website ?? '',
          description: activeBranch.description ?? '',
          address: activeBranch.address ?? '',
          city: activeBranch.city ?? '',
          province: activeBranch.province ?? '',
          country: activeBranch.country ?? 'República Dominicana',
          phone: activeBranch.phone ?? '',
          whatsapp: activeBranch.whatsapp ?? '',
          logo_url: activeBranch.logo_url ?? '',
          latitude: activeBranch.latitude != null ? String(activeBranch.latitude) : '',
          longitude: activeBranch.longitude != null ? String(activeBranch.longitude) : '',
        },
      );
      setDirty(!!draft);
      setLoadedBranchId(activeBranch.id);
      setSuccess(null);
      setError(null);
    } else {
      setForm(emptyForm);
      setDirty(false);
      setLoadedBranchId(null);
      setSuccess(null);
      setError(null);
    }
  }, [activeBranch]);

  // Guardar automáticamente el borrador para no perder lo que llevas escrito.
  useEffect(() => {
    if (!dirty) return;
    if (draftKey && activeBranch && activeBranch.id === loadedBranchId) {
      saveDraft(draftKey, form);
    }
  }, [form, dirty, draftKey, activeBranch, loadedBranchId]);

  // Avisar al navegador si intenta cerrar/recargar con cambios sin guardar.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const set = (key: keyof BranchForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleLogoUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `branch-logo-${Date.now()}.${ext}`;
      const { error: e } = await supabase.storage.from('products').upload(path, file);
      if (e) throw e;
      const { data } = supabase.storage.from('products').getPublicUrl(path);
      set('logo_url', data.publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeBranchId) return;
    if (!form.name.trim()) {
      setError('El nombre comercial es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: e2 } = await supabase
        .from('branches')
        .update({
          name: form.name.trim(),
          legal_name: form.legal_name.trim() || null,
          rnc: form.rnc.trim() || null,
          email: form.email.trim() || null,
          website: form.website.trim() || null,
          description: form.description.trim() || null,
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          province: form.province.trim() || null,
          country: form.country.trim() || null,
          phone: form.phone.trim() || null,
          whatsapp: form.whatsapp.trim() || null,
          logo_url: form.logo_url.trim() || null,
          latitude: form.latitude.trim() ? Number(form.latitude) : null,
          longitude: form.longitude.trim() ? Number(form.longitude) : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', activeBranchId);
      if (e2) throw e2;
      setSuccess('Datos de la sucursal guardados correctamente.');
      setDirty(false);
      if (draftKey) clearDraft(draftKey);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  const hasCoords = form.latitude.trim() !== '' && form.longitude.trim() !== '';
  const mapSrc = hasCoords
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${form.latitude},${form.longitude}`)}&z=15&output=embed`
    : '';
  const mapsLink = hasCoords
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${form.latitude},${form.longitude}`)}`
    : '';

  return (
    <div className="max-w-3xl">
      <SettingsBreadcrumb label="Datos de la sucursal" icon="ri-store-2-line" />
      <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Datos de la sucursal
      </h1>
      <p className="mt-2 text-foreground-600">
        Cada sucursal tiene su propia información comercial, fiscal y de ubicación. Se usará
        automáticamente en las facturas de esa sucursal.
      </p>

      {/* Selector de sucursal */}
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
        <label className={labelClass}>
          <i className="ri-building-2-line mr-1" /> Sucursal a configurar
        </label>
        <select
          value={activeBranchId ?? ''}
          onChange={(e) => setActiveBranchId(e.target.value || null)}
          className={inputClass}
        >
          <option value="">Selecciona una sucursal</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {!activeBranchId ? (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 p-10 text-center">
          <i className="ri-building-2-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">
            Selecciona una sucursal para editar sus datos.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-2 text-sm text-foreground-700">
              <i className="ri-checkbox-circle-line text-accent-700" /> {success}
            </div>
          )}

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-4">
            <h2 className="font-heading font-semibold text-foreground-950">Datos comerciales y fiscales</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nombre comercial *</label>
                <input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Razón social</label>
                <input className={inputClass} value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>RNC</label>
                <input className={inputClass} value={form.rnc} onChange={(e) => set('rnc', e.target.value)} placeholder="Ej. 123456789" />
              </div>
              <div>
                <label className={labelClass}>Correo</label>
                <input className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Sitio web</label>
                <input className={inputClass} value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <label className={labelClass}>Teléfono</label>
                <input className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>WhatsApp</label>
                <input className={inputClass} value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder="18095551234" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Descripción / mensaje de la factura</label>
              <input className={inputClass} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Ej. ¡Gracias por su compra!" />
            </div>
            <div>
              <label className={labelClass}>Logo</label>
              <div className="flex items-center gap-3">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="w-16 h-16 rounded-md object-contain bg-background-100 shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-md border border-dashed border-background-300 bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
                    <i className="ri-image-line text-xl" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 transition cursor-pointer">
                    <i className="ri-upload-line" />
                    {uploading ? 'Subiendo...' : 'Subir logo'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
                  </label>
                  <input className={inputClass} value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} placeholder="o pega una URL" />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-4">
            <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-map-pin-line text-primary-700" /> Ubicación
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Dirección</label>
                <input className={inputClass} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Calle, número, sector" />
              </div>
              <div>
                <label className={labelClass}>Ciudad</label>
                <input className={inputClass} value={form.city} onChange={(e) => set('city', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Provincia</label>
                <input className={inputClass} value={form.province} onChange={(e) => set('province', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>País</label>
                <input className={inputClass} value={form.country} onChange={(e) => set('country', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Latitud</label>
                  <input className={inputClass} value={form.latitude} onChange={(e) => set('latitude', e.target.value)} placeholder="18.5601" />
                </div>
                <div>
                  <label className={labelClass}>Longitud</label>
                  <input className={inputClass} value={form.longitude} onChange={(e) => set('longitude', e.target.value)} placeholder="-68.3725" />
                </div>
              </div>
            </div>

            <div className="rounded-md border border-background-200 overflow-hidden">
              {hasCoords ? (
                <iframe
                  title="Ubicación de la sucursal"
                  src={mapSrc}
                  width="100%"
                  height="240"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                />
              ) : (
                <div className="h-[240px] flex flex-col items-center justify-center text-center text-foreground-500 bg-background-100">
                  <i className="ri-map-2-line text-3xl" />
                  <p className="mt-2 text-sm">Ingresa latitud y longitud para ver el mapa.</p>
                  <p className="text-xs text-foreground-400 mt-1">
                    Consejo: busca tu dirección en Google Maps y copia las coordenadas.
                  </p>
                </div>
              )}
            </div>
            {hasCoords && (
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
              >
                <i className="ri-external-link-line" /> Ver en Google Maps
              </a>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              <i className="ri-save-line" />
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      )}

      <Modal
        open={blocker.state === 'blocked'}
        onClose={blocker.reset}
        title="Cambios sin guardar"
        footer={
          <>
            <button
              type="button"
              onClick={blocker.reset}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Quedarme y seguir editando
            </button>
            <button
              type="button"
              onClick={blocker.proceed}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              Salir sin guardar
            </button>
          </>
        }
      >
        Tienes cambios sin guardar en esta sucursal. Si sales ahora, esos cambios no se
        aplicarán. Guardé tu avance para que puedas retomarlo al volver.
      </Modal>
    </div>
  );
}